import { useEffect, useMemo, useRef, useState } from 'react';

import { QueryStats, Visibility, VisibilityOff } from '@mui/icons-material';
import {
  Box,
  Button,
  Grid,
  Paper,
  Skeleton,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useQuery } from 'react-query';
import { useHistory, useLocation } from 'react-router-dom';
import CardDemonstration from 'src/components/CardDemonstration/CardDemonstration';
import MetaTags from 'src/components/Seo/MetaTags';
import TabTemplate from 'src/components/UI/TabTemplate';
import { useAuthContext } from 'src/context/AuthContextProvider';
import {
  getInformativeTextByLocation,
  getPayWallBanner,
} from 'src/services/informative';
import {
  getProfitabilityData,
  getProfitabilityFilters,
} from 'src/services/profitability';
import {
  INITIAL_PROFITABILITY_STATE,
  ProfitabilityFilter,
  Profitability as ProfitabilityType,
  State,
} from 'src/types/profitability';
import { useDebounce } from 'src/utils/hooks';
import { ScrollToTop } from 'src/utils/scroll';

import { ProfitabilityFilters } from './ProfitabilityFilters';
import { ScatterPlotWithRegression } from './ScatterPlotWithRegressionChart';

const PERIOD_OPTIONS = [
  { label: '7 dias', value: 7 },
  { label: '15 dias', value: 15 },
  { label: '30 dias', value: 30 },
  { label: 'Tudo', value: null },
];

const FILTER_URL_KEYS: Array<keyof State> = [
  'mercado',
  'search',
  'ativo_lastro',
  'metodo',
  'tipo_devedor',
  'pulverizacao',
  'classificacao_risco',
];

const PERIOD_DEFAULT = 7;
const PERIOD_PARAM_ALL = 'all';
const PERIOD_PARAM_VALUES = ['7', '15', '30'];

type PeriodRange = number | null;

const isEmptyFilterValue = (value: State[keyof State]) =>
  value === '' || value === null || value === undefined;

const parseFilterValueFromSearch = (
  key: keyof State,
  paramValue: string | null,
) => {
  if (paramValue === null) return INITIAL_PROFITABILITY_STATE[key];

  if (key === 'pulverizacao') {
    if (paramValue === 'true') return true;
    if (paramValue === 'false') return false;
  }

  return paramValue;
};

const parseStateFromSearch = (search: string): State => {
  const params = new URLSearchParams(search);

  return FILTER_URL_KEYS.reduce<State>(
    (parsedState, key) => {
      const paramValue = params.get(key);

      return {
        ...parsedState,
        [key]: parseFilterValueFromSearch(key, paramValue),
      };
    },
    { ...INITIAL_PROFITABILITY_STATE },
  );
};

const parsePeriodFromSearch = (search: string): PeriodRange => {
  const period = new URLSearchParams(search).get('period');

  if (period === PERIOD_PARAM_ALL) return null;
  if (period && PERIOD_PARAM_VALUES.includes(period)) return Number(period);

  return PERIOD_DEFAULT;
};

const buildSearchFromFilters = (state: State, periodRange: PeriodRange) => {
  const params = new URLSearchParams();
  const mercado = isEmptyFilterValue(state.mercado)
    ? INITIAL_PROFITABILITY_STATE.mercado
    : state.mercado;
  const metodo = isEmptyFilterValue(state.metodo)
    ? INITIAL_PROFITABILITY_STATE.metodo
    : state.metodo;

  FILTER_URL_KEYS.forEach((key) => {
    const value = state[key];

    if (key === 'mercado') {
      params.set(key, mercado);
      return;
    }

    if (key === 'metodo') {
      params.set(key, metodo);
      return;
    }

    if (!isEmptyFilterValue(value)) {
      params.set(key, String(value));
    }
  });

  if (periodRange === null) {
    params.set('period', PERIOD_PARAM_ALL);
  } else {
    params.set('period', String(periodRange ?? PERIOD_DEFAULT));
  }

  return params.toString();
};

const areProfitabilityStatesEqual = (firstState: State, secondState: State) =>
  FILTER_URL_KEYS.every((key) => firstState[key] === secondState[key]);

export default function Profitability() {
  const [showTrend, setShowTrend] = useState(false);
  const location = useLocation();
  const history = useHistory();
  const [periodRange, setPeriodRange] = useState<PeriodRange>(() =>
    parsePeriodFromSearch(location.search),
  );
  const [data, setData] = useState<ProfitabilityType[]>([]);
  const { auth } = useAuthContext();
  const filtersRef = useRef<ProfitabilityFilter>();
  const isExtraSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down('sm'),
  );
  const hasPermission =
    !!auth?.user?.subscription?.plan?.permissions?.rentabilidade;

  const [state, setState] = useState<State>(() =>
    parseStateFromSearch(location.search),
  );
  const debouncedSearch = useDebounce(state.search, 500);
  const dataQueryState = useMemo(
    () => ({ ...state, search: debouncedSearch }),
    [debouncedSearch, state],
  );

  const { data: description } = useQuery('profitabilityDescription', () =>
    getInformativeTextByLocation('profitability'),
  );

  const { data: contentPayWallBanner } = useQuery(
    'paywall',
    () => getPayWallBanner('profitability'),
    {
      enabled: !auth.isLoading,
    },
  );

  useEffect(() => {
    const nextState = parseStateFromSearch(location.search);
    const nextPeriodRange = parsePeriodFromSearch(location.search);

    setState((currentState) =>
      areProfitabilityStatesEqual(currentState, nextState)
        ? currentState
        : nextState,
    );
    setPeriodRange((currentPeriodRange) =>
      currentPeriodRange === nextPeriodRange
        ? currentPeriodRange
        : nextPeriodRange,
    );
  }, [location.search]);

  useEffect(() => {
    const search = buildSearchFromFilters(state, periodRange);

    if (search !== location.search.replace(/^\?/, '')) {
      history.replace({
        pathname: location.pathname,
        search,
      });
    }
  }, [history, location.pathname, location.search, periodRange, state]);

  const { isLoading: isLoadingFilters, data: filtersData } = useQuery(
    ['profitabilityFilters', state.mercado],
    () => getProfitabilityFilters(state.mercado),
    {
      enabled: hasPermission && !auth.isLoading,
      onSuccess: (data: ProfitabilityFilter) => {
        filtersRef.current = data;
      },
    },
  );

  const { isLoading: isLoadingData } = useQuery(
    ['profitabilityData', dataQueryState, periodRange],
    () => getProfitabilityData(dataQueryState, periodRange),
    {
      enabled: !!filtersData && !isLoadingFilters,
      onSuccess: (response) => {
        setData(response.data);
      },
    },
  );

  const isLoading = isLoadingFilters || isLoadingData;

  return (
    <ScrollToTop>
      <MetaTags
        title="Rentabilidades"
        description="Analise a rentabilidade dos mercados de CRI e CRA, instrumentos financeiros essenciais no setor imobiliário e do agronegócio. Compare o desempenho desses títulos ao longo do tempo, considerando fatores como taxas de juros, prazos e riscos associados, para tomar decisões de investimento mais informadas."
      />
      <TabTemplate
        title="Rentabilidades"
        subtitle={description?.data?.profitability?.informationText ?? ''}
        breadcrumbs1="Rentabilidades"
        titleIcon={<QueryStats />}
        tabContent={[]}
        tabIndex={0}
        handleChange={() => {}}
      />
      <Box component="section" position={!hasPermission ? 'relative' : 'unset'}>
        <Paper
          elevation={2}
          sx={{
            p: 2,
            borderRadius: '5px',
            minHeight: isExtraSmall && !hasPermission ? 420 : '100%',
          }}>
          {!hasPermission ? (
            <CardDemonstration
              title={contentPayWallBanner?.profitability?.title}
              subTitle={contentPayWallBanner?.profitability?.subTitle}
              labelButton={contentPayWallBanner?.profitability?.labelButton}
              url={contentPayWallBanner?.profitability?.url}
            />
          ) : (
            <Box p={1} borderRadius={'5px'}>
              <Stack spacing={2}>
                <ProfitabilityFilters
                  disabled={!hasPermission}
                  state={state}
                  filters={filtersRef.current}
                  setState={setState}
                  isLoading={isLoadingFilters}
                />

                <Button
                  size="small"
                  variant={showTrend ? 'contained' : 'outlined'}
                  color="primary"
                  sx={{
                    height: '40px',
                    width: { xs: '100%', sm: 'fit-content' },
                    minWidth: 152,
                    alignSelf: { xs: 'stretch', sm: 'flex-start' },
                  }}
                  disabled={isLoading || !hasPermission}
                  onClick={() => setShowTrend(!showTrend)}
                  startIcon={showTrend ? <VisibilityOff /> : <Visibility />}>
                  Tendência
                </Button>

                <Stack
                  spacing={1}
                  justifyContent={'center'}
                  alignItems={'center'}
                  minHeight={120}>
                  <Box pt={1} width={'100%'}>
                    <Grid container spacing={1} justifyContent="flex-end">
                      {PERIOD_OPTIONS.map(({ label, value }) => (
                        <Grid item key={value}>
                          <Typography
                            variant="subtitle1"
                            onClick={() => setPeriodRange(value)}
                            sx={{
                              borderBottom:
                                periodRange === value
                                  ? '2px solid #FF8211'
                                  : 'none',
                              pointerEvents: !hasPermission ? 'none' : 'auto',
                            }}
                            style={{ cursor: 'pointer' }}>
                            {label}
                          </Typography>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                  {isLoading ? (
                    <Skeleton variant="rounded" width={'100%'} height={350} />
                  ) : (
                    <ScatterPlotWithRegression
                      data={data}
                      width={500}
                      height={220}
                      showRegression={showTrend}
                    />
                  )}
                </Stack>
              </Stack>
            </Box>
          )}
        </Paper>
      </Box>
    </ScrollToTop>
  );
}
